import { IScope } from 'angular'
import findIndex from 'lodash/findIndex'
import moment from 'moment-timezone'
import { ApiOutlet, ApiRegister } from '@vend/types'

import { EVENTS } from '../../constants'
import { RegisterManager } from '../../managers/register-manager'
import { UserManager } from '../../managers/user-manager'
import { WebRegisterStatusManager } from '../../managers/web-register-status-manager'

/**
 * Controller class for the select register modal
 *
 * @class SelectRegisterController
 */
export class SelectRegisterController {
  /**
   * The current active register.
   */
  public activeRegister: $TSFixMe
  public dismissible: boolean

  /**
   * Collection of outlet and register info containing an `outlet` property with the outlet info and a `registers`
   * property with the array of registers for that outlet.
   */
  public registersByOutlet: $TSFixMe[] = []

  /**
   * The index number of the selected tab. Defaults to zero to select the first outlet in the provided array.
   */
  public selectedOutlet = 0

  constructor(
    $scope: IScope,
    public close: $TSFixMe,
    public options: $TSFixMe,
    private registerManager: RegisterManager,
    private userManager: UserManager,
    webRegisterStatusManager: WebRegisterStatusManager
  ) {
    'ngInject'
    this.dismissible = options.dismissible || false

    this.activeRegister = registerManager.getActiveRegister()
    this._setRegistersByOutlet()

    userManager.on('loggedInUserChange', this._setRegistersByOutlet, this)
    webRegisterStatusManager.on(EVENTS.statusChange, this.close, this)

    $scope.$on('$destroy', () => {
      userManager.off('loggedInUserChange', this._setRegistersByOutlet)
      webRegisterStatusManager.off(EVENTS.statusChange, this.close)
    })
  }

  /**
   * Select the given register as the active register. Triggers the open register workflow.
   *
   * @method selectRegister
   *
   * @param register {Register}
   *        The register selected by the user
   *
   * @param outlet {Outlet}
   *        The outlet the selected register belongs to
   */
  public selectRegister = (register: ApiRegister, outlet: ApiOutlet) => {
    if (!this.activeRegister || this.activeRegister.id !== register.id) {
      this.registerManager.setActiveRegister(register, outlet)
    }

    this.close()
  }

  /**
   * Get the register trading status; either the opening or closing time.
   *
   * @method getRegisterTradingStatus
   *
   * @param register {Register}
   *        The register selected by the user
   *
   * @param outlet {Outlet}
   *        The outlet the selected register belongs to
   *
   * @return {String} The opening or closing date and time of the provided register
   */
  public getRegisterTradingStatus(register: ApiRegister, outlet: ApiOutlet) {
    function formatDateTime(dateTime: string | null) {
      return moment(dateTime).tz(outlet.time_zone).format('h:mma, Do MMM')
    }

    if (register.is_open) {
      return `Opened at ${formatDateTime(register.register_open_time)}`
    }

    if (register.register_close_time !== null) {
      return `Closed at ${formatDateTime(register.register_close_time)}`
    } else {
      return 'Closed'
    }
  }

  /**
   * Sets the available outlets and registers.
   *
   * @private
   * @method _setRegistersByOutlet
   */
  private _setRegistersByOutlet() {
    const activeUser = this.userManager.getLoggedInUser()
    this.registerManager
      .getAllRegistersByOutlet({
        restrictedOutlets: activeUser.restricted_outlet_ids!,
      })
      .then((registersByOutlet: $TSFixMe) => {
        this.registersByOutlet = registersByOutlet
        this._setTabForActiveOutlet()
      })
  }

  /**
   * Set the active outlet tab that is associated with the current active register.
   *
   * @private
   * @method _setTabForActiveOutlet
   *
   * @return {Number} The array index of the matching outlet
   */
  private _setTabForActiveOutlet() {
    const activeOutlet = this.registerManager.getActiveOutlet()
    let selectedOutletIndex = 0

    if (activeOutlet) {
      selectedOutletIndex = findIndex(
        this.registersByOutlet,
        registerByOutlet => registerByOutlet.outlet.id === activeOutlet.id
      )
    }

    this.selectedOutlet = selectedOutletIndex !== -1 ? selectedOutletIndex : 0
  }
}
